/** Marketing i18n fragment — pricingPage (the public /pricing page). */

export const pricingPageEn = {
  meta: {
    title: "Pricing",
    description:
      "Pricing for governed AI workflows: approved ticket replies, support-to-engineering remediation, and deployment approvals across Slack, Teams, Freshdesk, Jira, and GitHub. Starter and Team are free to start.",
  },
  hero: {
    eyebrow: "Pricing",
    title: "Plans priced by",
    highlight: "what the AI is allowed to do",
    subtitle:
      "Start free: AI answers and drafts, your team approves every send. Move up when you also want code changes and deployments prepared, verified and approved in the same flow.",
    primaryCta: "Create a free workspace",
    secondaryCta: "Book a demo",
    footnote:
      "Every plan runs on the same core of rules, checks and approvals (governance). By default, nothing is written to a helpdesk, repository or deployment without a recorded approval, and connection health is reported per function — replies, ticket reads, pull requests — not as one green checkmark.",
  },
  positioning: {
    eyebrow: "How the plans fit",
    title: "Plans are scoped by workflow, not seat count",
    description:
      "Starter and Team govern support replies. Pro adds code changes and pull requests. Enterprise adds deployment approvals, custom policy, and identity controls.",
    cards: {
      starter: {
        name: "Starter",
        body: "Ticket replies drafted by AI and approved by someone on your team, on one helpdesk or tracker.",
      },
      team: {
        name: "Team",
        body: "Support teams working across Slack, Teams, Freshdesk, and Jira, with visibility into every outbound write.",
      },
      pro: {
        name: "Pro",
        body: "Turns support issues into reviewed code changes, with test results attached and approval gates on risky pull requests.",
      },
      enterprise: {
        name: "Enterprise",
        body: "For organizations with strict security requirements: SSO (OIDC), SCIM 2.0, custom policies, and deployment approvals.",
      },
    },
  },
  example: {
    eyebrow: "In practice",
    title: "What AI actions look like in euros",
    description:
      "One AI action is one unit of AI work: a proposed reply, a ticket-question answer, a classification, or a remediation step. Two indicative scenarios to estimate your own usage — the real number depends on how many tickets you route through FixControl.",
    small: {
      title: "≈ 100 tickets a month",
      body: "A team that routes about 100 tickets a month through FixControl typically uses two actions per ticket — a classification plus a proposed reply. That is roughly 200–250 actions, which fits Starter at €99 per month.",
    },
    medium: {
      title: "≈ 350 tickets a month",
      body: "A team routing about 350 tickets a month, with occasional re-drafts and ticket questions on top, lands around 800–1,000 actions. That fits Team at €299 per month.",
    },
    limit: {
      title: "What happens at the limit",
      body: "New AI actions are declined with a clear message until the limit resets or is raised. Nothing already in flight is dropped, and there is no automatic overage billing — your invoice does not grow behind your back.",
    },
    footnote:
      "Indicative scenarios, not a quote. Approving or rejecting a result never counts as an action, and routine reads and audit history are free.",
  },
  compare: {
    eyebrow: "Compare plans",
    title: "The full feature matrix, caveats included",
    description:
      "A check mark means the capability ships as described. Where a feature depends on your setup, such as Argo deployment actions, the cell says so.",
  },
  trialSection: {
    eyebrow: "Get started",
    title: "Starter and Team are self-serve; Pro and Enterprise start with a pilot",
    description:
      "Support workflows are self-serve. Code and deployment workflows start with guided onboarding, because repository access, CI/CD, and policy boundaries are scoped together with your team before the AI touches them. From there, usage expands across seats, integrations and governed workflows.",
  },
  trial: {
    starterTeam: {
      title: "Starter & Team — free, self-serve",
      b1: "Free to start; no credit card",
      b2: "Demo data included, so you can evaluate before connecting anything",
      b3: "Connect one live integration when you are ready",
      b4: "Customer-facing replies always require an approval",
    },
    proEnterprise: {
      title: "Pro & Enterprise — guided pilot",
      b1: "Starts with an integration workshop",
      b2: "Success criteria agreed up front",
      b3: "Repository, CI/CD, and policy boundaries scoped with your team",
      b4: "Credited toward an annual contract if you continue",
    },
  },
  faqSection: {
    eyebrow: "FAQ",
    title: "Pricing questions",
  },
  note: "By default, nothing FixControl does reaches a customer of your organization or your production environment without a recorded human decision. Any autonomy, such as the optional low-risk email mode, is explicit, opt-in, policy-bounded and audit-logged.",
  related: {
    title: "The workflows behind these plans",
    ticketQuestions: {
      label: "Slack & Jira ticket questions",
      description: "Ask about open work, status, and priority in Slack instead of filing another ticket.",
    },
    approvedReplies: {
      label: "Approved replies",
      description: "AI drafts the reply; someone on your team approves exactly what is sent to Freshdesk or Jira.",
    },
    remediation: {
      label: "Support → engineering remediation",
      description: "How a support issue becomes a reviewed code change, with patch evidence.",
    },
    approvalLedger: {
      label: "AI approval ledger",
      description: "Actor, role, channel and timestamp on every governed action.",
    },
    deployment: {
      label: "Deployment governance",
      description: "Approve, promote or abort deployments where Argo/Kubernetes is configured.",
    },
    jira: {
      label: "Jira integration",
      description: "Read, search and write with capability-level health.",
    },
    freshdesk: {
      label: "Freshdesk integration",
      description: "Approved customer replies and two-way ticket sync.",
    },
    slack: {
      label: "Slack integration",
      description: "Intake, ticket questions, approvals and decision cards in Slack.",
    },
    security: {
      label: "Security & governance",
      description: "Tenant isolation, two-factor authentication, audit export, SSO (OIDC), SCIM, and retention controls.",
    },
  },
  cta: {
    title: "Put your first AI reply behind an approval",
    description:
      "Starter and Team are free to start. For code and deployment workflows, book a pilot and we will scope the boundaries with you.",
    primary: "Create a free workspace",
    secondary: "Book a demo",
  },
  tiers: {
    monthly: "Monthly",
    annual: "Annual",
    twoMonthsFree: "2 months free",
    recommended: "Recommended",
    allPricesNote:
      "All prices in euros, excluding VAT (EU B2B). Annual billing is two months free compared with paying monthly.",
    enforcementNote:
      "Plan limits are enforced through your contract while in-product enforcement rolls out.",
    bestFor: "Best for: ",
    everythingIn: "Everything in {plan}, plus:",
    perMonth: "/ mo",
    perYear: "/ year",
    custom: "Custom",
    customTailored: "Tailored to your environment",
    annualHint: "≈ {amount} / mo, billed annually · excl. VAT",
    orPerYear: "or {amount} / year · excl. VAT",
    customAnnual: "Custom annual pricing · excl. VAT",
    from: "From",
    featuresColumn: "Features",
  },
  plans: {
    starter: {
      name: "Starter",
      positioning: "AI drafts ticket replies; your team approves each send.",
      forWho: "Support teams adding approval-gated AI to one helpdesk.",
      ctaLabel: "Start free",
      ctaNote: "Free to start. Replies go out only after approval.",
      limitsUsers: "Up to 3 users",
      limitsAiActions: "250 AI actions / month",
      f1: "1 helpdesk/tracker integration: Freshdesk, Jira, Odoo or TOPdesk",
      f2: "Slack or website chat intake",
      f3: "Ticket-question answering: open, status, priority, awaiting approval",
      f4: "AI-proposed replies with human edit and approval before send",
      f5: "Basic audit timeline",
      f6: "Capability-level integration health",
    },
    team: {
      name: "Team",
      positioning: "Approved AI replies across Slack, Teams, Freshdesk, and Jira.",
      forWho: "Support teams working across chat and ticket trackers every day.",
      ctaLabel: "Start free",
      ctaNote: "Free to start. Connect one live integration during setup.",
      limitsUsers: "Up to 10 users",
      limitsAiActions: "1,000 AI actions / month",
      f1: "Freshdesk, Jira, Odoo + TOPdesk integrations",
      f2: "Slack + website chat (Microsoft Teams included)",
      f3: "Approved AI replies to Freshdesk, Jira, Odoo and TOPdesk",
      f4: "Ticket-question answering across FixControl, Jira, Freshdesk, Odoo and TOPdesk",
      f5: "Thread-aware Slack/Teams context",
      f6: "Full operational timeline",
      f7: "Outbox / retry / dead-letter visibility for external writes",
      f8: "Integration health by capability",
    },
    pro: {
      name: "Pro",
      positioning: "From support ticket to reviewed pull request.",
      forWho: "Teams shipping AI-proposed code changes through review gates.",
      ctaLabel: "Book a pilot",
      ctaNote: "Pilots start by scoping repository and CI/CD access.",
      limitsUsers: "Up to 25 users",
      limitsAiActions: "5,000 AI actions / month",
      f1: "GitHub / GitLab integration",
      f2: "Engineering missions and codebase analysis",
      f3: "Patch / diff evidence with tests, QA and security evidence",
      f4: "PR / MR workflow with approval gates for risky changes",
      f5: "Deployment approval evidence where configured",
      f6: "Multiple projects",
      f7: "Audit export and advanced audit features",
    },
    enterprise: {
      name: "Enterprise",
      positioning: "Custom policies, identity controls, and deployment governance.",
      forWho: "Organizations with strict security and audit requirements.",
      ctaLabel: "Talk to us",
      ctaNote: "Custom pricing. Book an enterprise demo.",
      limitsUsers: "Custom user limits",
      limitsAiActions: "Custom AI action limits",
      f1: "SSO (OIDC) + SCIM 2.0; SAML on the roadmap",
      f2: "Custom policies and platform-admin controls",
      f3: "Tenant / admin separation and custom retention",
      f4: "Private deployment or dedicated environment, scoped per engagement",
      f5: "Kubernetes / Argo deployment governance",
      f6: "SLA and support terms, plus security-review assistance",
      f7: "Custom onboarding",
    },
  },
  comparison: {
    groups: {
      intake: { title: "Intake and ticket questions" },
      helpdesk: { title: "Helpdesk / tracker workflows" },
      governance: { title: "Governance" },
      engineering: { title: "Engineering remediation" },
      deployment: { title: "Deployment governance" },
      enterprise: { title: "Enterprise controls" },
    },
    rows: {
      websiteChatIntake: { label: "Website chat intake" },
      slackIntake: { label: "Slack intake" },
      teamsIntake: { label: "Microsoft Teams intake" },
      ticketQuestionAnswering: { label: "Ticket-question answering" },
      threadAwareFollowups: { label: "Thread-aware follow-ups" },
      channelRedaction: { label: "Public / shared channel redaction" },
      freshdeskIntegration: { label: "Freshdesk integration" },
      jiraIntegration: { label: "Jira integration" },
      odooIntegration: { label: "Odoo integration" },
      topdeskIntegration: { label: "TOPdesk integration" },
      aiProposedReplies: { label: "AI proposed replies" },
      humanEditBeforeSend: { label: "Human edit before send" },
      freshdeskCustomerReply: { label: "Freshdesk customer reply" },
      odooCustomerReply: { label: "Odoo customer reply" },
      topdeskCustomerReply: { label: "TOPdesk customer reply" },
      jiraInternalComment: { label: "Jira internal comment" },
      jsmCustomerReply: {
        label: "Jira Service Management customer reply",
        caveat: "Requires a configured Jira Service Management request type with a healthy customer-reply capability. Every reply is safety-checked and human-approved.",
      },
      ticketWriteOutbox: { label: "Ticket status / comment / write outbox" },
      approvalLedger: { label: "Approval ledger" },
      actorRoleChannel: { label: "Actor / role / channel / timestamp" },
      operationalTimeline: { label: "Operational timeline" },
      capabilityHealth: { label: "Capability-level health" },
      outboxRetryDlq: { label: "Outbox / retry / dead-letter" },
      auditExport: { label: "Audit export" },
      githubGitlab: { label: "GitHub / GitLab" },
      codebaseAnalysis: { label: "Codebase analysis" },
      patchDiffEvidence: { label: "Patch / diff evidence" },
      testsQaSecurity: { label: "Tests / QA / security evidence" },
      prMrWorkflow: { label: "PR / MR workflow" },
      riskGates: { label: "Risk gates" },
      twoFactor: {
        label: "Two-factor authentication (TOTP)",
        caveat: "Authenticator app + recovery codes, on every plan. FixControl platform staff always need it; your own admins and members need it as soon as your organization requires it. No SMS.",
      },
      argoVisibility: { label: "Kubernetes / Argo visibility" },
      deploymentApproval: { label: "Deployment approval" },
      promoteAbortRead: {
        label: "Promote / abort / read status",
        caveat: "Where configured for your Argo / Kubernetes setup.",
      },
      failureVisibility: { label: "Failure visibility" },
      sso: {
        label: "SSO (OIDC)",
        caveat: "SAML is on the roadmap.",
      },
      scim: {
        label: "SCIM",
        caveat: "SCIM 2.0, for user provisioning and deprovisioning.",
      },
      customPolicies: { label: "Custom policies" },
      privateDeployment: {
        label: "Private deployment",
        caveat: "Scoped per engagement.",
      },
      slaSecurityReview: { label: "SLA / security review" },
    },
  },
  cells: {
    whereConfigured: "where configured",
    whereSupported: "requires setup",
    whereImplemented: "Included",
    oneOfFreshdeskJira: "1 of Freshdesk / Jira / Odoo / TOPdesk",
    basic: "Basic",
  },
  faq: {
    q1: {
      q: "Is it free to get started?",
      a: "Yes. Starter and Team are free to start, self-serve, with demo data and one live integration included. Customer-facing replies still require approval.",
    },
    q2: {
      q: "Do you charge per seat?",
      a: "Plans include a set number of users (3 on Starter, 10 on Team, 25 on Pro) rather than per-seat pricing. Higher user limits are available on Enterprise or by request.",
    },
    q3: {
      q: "What counts as an AI action?",
      a: "One AI action is one unit of AI work: a proposed or re-drafted reply, a ticket-question answer, a classification, or a remediation/mission step. Work the AI performs counts even if you reject the result; approving or rejecting itself does not count, and routine reads and viewing audit history are free. Tokens are never metered.",
    },
    q4: {
      q: "Can we start with only Freshdesk, Jira, Odoo or TOPdesk?",
      a: "Yes. Starter includes one helpdesk or tracker integration: Freshdesk, Jira, Odoo or TOPdesk. Team and above include all four, and you can connect more as you grow.",
    },
    q5: {
      q: "Does FixControl send replies automatically?",
      a: "By default, no. Freshdesk and Jira replies always require a human approval. For email, an admin can opt in to a policy-scoped low-risk auto-send mode, bounded by confidence thresholds, rate limits, an org-wide ceiling, and audit logging of every send.",
    },
    q6: {
      q: "Does Jira customer reply work for every Jira project?",
      a: "Customer-visible replies require Jira Service Management. On Jira Software projects, FixControl adds internal issue comments instead.",
    },
    q7: {
      q: "What happens if Slack, Jira or Freshdesk fails?",
      a: "Outbound writes go through an outbox with retry and dead-letter handling, and integration health is tracked per capability. When a write fails, FixControl shows the failure and exactly what it could not complete.",
    },
    q8: {
      q: "Can FixControl deploy to production?",
      a: "Not on its own. Where Kubernetes / Argo is configured, FixControl shows deployment status and routes the deployment approval to someone on your team. Promote, abort, and read actions run only after that approval.",
    },
    q9: {
      q: "Is public website chat allowed to see internal tickets?",
      a: "No. Public website chat sees only public, customer-safe knowledge and project-safe summaries, never your organization’s internal data. Ticket questions about internal work are answered only in authenticated Slack, Teams, and in-app surfaces.",
    },
    q10: {
      q: "Can we self-host or run on a private VPS?",
      a: "Yes, on Enterprise. Private deployment to a dedicated environment or VPS is scoped per engagement; tell us about your environment and security requirements.",
    },
    q11: {
      q: "Do you offer annual contracts?",
      a: "Yes. Annual billing is two months free compared with paying monthly. Prices are in euros, excluding VAT (EU B2B).",
    },
    q12: {
      q: "Do you support SSO/SCIM?",
      a: "SSO (OIDC) and SCIM 2.0 are included on Enterprise; SAML is on the roadmap. Tell us your identity provider and we will confirm the setup.",
    },
    q13: {
      q: "What happens when we reach the monthly AI-action limit?",
      a: "New AI actions are declined with a clear message until the limit resets or is raised — nothing already in flight is dropped, and there is no automatic overage billing. Limits and any overage behavior are agreed contractually while in-product enforcement rolls out.",
    },
    q14: {
      q: "How do billing, contracts and cancellation work?",
      a: "Starter and Team workspaces are free to start; paid plans are invoiced. Contract length, cancellation terms and pilot terms are agreed during onboarding — monthly and annual billing are both available, and pilots are credited toward an annual contract if you continue.",
    },
    q15: {
      q: "What support is included?",
      a: "Every plan includes email support at contact@fixcontrol.ai. During onboarding and pilots you work directly with the team that builds and operates the product. Enterprise agreements add contractual SLA and support terms.",
    },
    q16: {
      q: "Can we buy from outside the EU?",
      a: "Yes. Prices are in euros and exclude VAT (EU B2B reverse charge applies within the EU). For organizations outside the EU, invoicing and tax handling are confirmed during onboarding.",
    },
    q17: {
      q: "Can FixControl scale with us?",
      a: "Yes. FixControl is designed as a scalable multi-tenant SaaS platform and can expand across seats, integrations, ticket volume and governed engineering workflows. Rollout starts with guided onboarding because the platform connects to sensitive operational systems; approval models, integration health and audit boundaries are validated with your team, and usage expands from there.",
    },
  },
};

export const pricingPageNl = {
  meta: {
    title: "Prijzen",
    description:
      "Prijzen voor gegoverneerde AI-workflows: goedgekeurde ticketantwoorden, support-naar-engineering-remediatie en deployment-goedkeuringen over Slack, Teams, Freshdesk, Jira en GitHub. Starter en Team zijn gratis te starten.",
  },
  hero: {
    eyebrow: "Prijzen",
    title: "Plannen geprijsd naar",
    highlight: "wat de AI mag doen",
    subtitle:
      "Begin gratis: AI beantwoordt en stelt op, jouw team keurt elke verzending goed. Stap over wanneer je ook codewijzigingen en deployments in dezelfde flow voorbereid, geverifieerd en goedgekeurd wilt hebben.",
    primaryCta: "Maak een gratis workspace",
    secondaryCta: "Boek een demo",
    footnote:
      "Elk plan draait op dezelfde kern van regels, controles en goedkeuring (governance). Standaard wordt er niets naar een helpdesk, repository of deployment geschreven zonder vastgelegde goedkeuring, en de gezondheid van elke koppeling wordt per functie gerapporteerd — antwoorden, tickets lezen, pull requests — niet als één groen vinkje.",
  },
  positioning: {
    eyebrow: "Hoe de plannen passen",
    title: "Plannen zijn afgebakend op workflow, niet op aantal seats",
    description:
      "Starter en Team governen support-antwoorden. Pro voegt codewijzigingen en pull requests toe. Enterprise voegt deployment-goedkeuringen, custom policy en identity-controls toe.",
    cards: {
      starter: {
        name: "Starter",
        body: "Ticketantwoorden die AI opstelt en die iemand van je team goedkeurt, op één helpdesk of tracker.",
      },
      team: {
        name: "Team",
        body: "Supportteams die werken over Slack, Teams, Freshdesk en Jira, met zicht op elke uitgaande schrijfactie.",
      },
      pro: {
        name: "Pro",
        body: "Zet support-issues om in gereviewde codewijzigingen, met patch-bewijs en goedkeuringspoorten op risicovolle PR's.",
      },
      enterprise: {
        name: "Enterprise",
        body: "Voor organisaties met strenge security-eisen: SSO (OIDC), SCIM 2.0, custom policies en deployment-goedkeuringen.",
      },
    },
  },
  example: {
    eyebrow: "In de praktijk",
    title: "Wat AI-acties betekenen in euro's",
    description:
      "Eén AI-actie is één eenheid AI-werk: een voorgesteld antwoord, een antwoord op een ticketvraag, een classificatie of een remediatiestap. Twee indicatieve scenario's om je eigen verbruik in te schatten — het echte aantal hangt af van hoeveel tickets je via FixControl laat lopen.",
    small: {
      title: "≈ 100 tickets per maand",
      body: "Een team dat ongeveer 100 tickets per maand via FixControl laat lopen, gebruikt doorgaans twee acties per ticket — een classificatie plus een voorgesteld antwoord. Dat is grofweg 200–250 acties, en dat past binnen Starter voor €99 per maand.",
    },
    medium: {
      title: "≈ 350 tickets per maand",
      body: "Een team met ongeveer 350 tickets per maand, met af en toe een herschreven antwoord en ticketvragen erbovenop, komt uit rond de 800–1.000 acties. Dat past binnen Team voor €299 per maand.",
    },
    limit: {
      title: "Wat er gebeurt bij de limiet",
      body: "Nieuwe AI-acties worden geweigerd met een duidelijke melding totdat de limiet reset of wordt verhoogd. Niets dat al loopt wordt afgebroken, en er is geen automatische overage-facturering — je factuur groeit niet achter je rug om.",
    },
    footnote:
      "Indicatieve scenario's, geen offerte. Goedkeuren of afwijzen van een resultaat telt nooit als actie, en routinematige leesacties en audithistorie zijn gratis.",
  },
  compare: {
    eyebrow: "Vergelijk plannen",
    title: "De volledige functiematrix, inclusief kanttekeningen",
    description:
      "Een vinkje betekent dat de capability werkt zoals beschreven. Waar een functie afhangt van je configuratie, zoals Argo-deployment-acties, zegt de cel dat.",
  },
  trialSection: {
    eyebrow: "Aan de slag",
    title: "Starter en Team zijn self-service; Pro en Enterprise beginnen met een pilot",
    description:
      "Support-workflows zijn self-service. Code- en deployment-workflows starten met begeleide onboarding, omdat repository-toegang, CI/CD en policy-grenzen samen met je team worden afgebakend voordat de AI eraan komt. Van daaruit breidt gebruik uit over seats, integraties en governed workflows.",
  },
  trial: {
    starterTeam: {
      title: "Starter & Team — gratis, self-service",
      b1: "Gratis starten, geen creditcard",
      b2: "Demodata inbegrepen, zodat je kunt evalueren voordat je iets koppelt",
      b3: "Koppel één echte integratie wanneer je er klaar voor bent",
      b4: "Klantgerichte antwoorden vereisen altijd een goedkeuring",
    },
    proEnterprise: {
      title: "Pro & Enterprise — begeleide pilot",
      b1: "Begint met een integratieworkshop",
      b2: "Succescriteria vooraf afgesproken",
      b3: "Repository-, CI/CD- en policy-grenzen samen met je team afgebakend",
      b4: "Verrekend met een jaarcontract als je doorgaat",
    },
  },
  faqSection: {
    eyebrow: "FAQ",
    title: "Vragen over prijzen",
  },
  note: "Standaard bereikt niets wat FixControl doet een klant van je organisatie of je productieomgeving zonder een vastgelegde menselijke beslissing. Elke vorm van autonomie, zoals de optionele laag-risico-e-mailmodus, is expliciet, opt-in, policy-begrensd en audit-gelogd.",
  related: {
    title: "De workflows achter deze plannen",
    ticketQuestions: {
      label: "Slack- & Jira-ticketvragen",
      description: "Vraag in Slack naar openstaand werk, status en prioriteit in plaats van nóg een ticket aan te maken.",
    },
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "AI stelt het antwoord op; een persoon keurt precies goed wat naar Freshdesk of Jira gaat.",
    },
    remediation: {
      label: "Support → engineering-remediatie",
      description: "Hoe een support-issue een gereviewde codewijziging wordt, met patch-bewijs.",
    },
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Actor, rol, kanaal en tijdstempel op elke gegoverneerde actie.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Keur deployments goed, promoot of breek ze af waar Argo/Kubernetes is geconfigureerd.",
    },
    jira: {
      label: "Jira-integratie",
      description: "Lezen, doorzoeken en schrijven met health op capability-niveau.",
    },
    freshdesk: {
      label: "Freshdesk-integratie",
      description: "Goedgekeurde klantantwoorden en tweerichtings-ticketsync.",
    },
    slack: {
      label: "Slack-integratie",
      description: "Intake, ticketvragen, goedkeuringen en beslis-kaarten in Slack.",
    },
    security: {
      label: "Security & governance",
      description: "Tenantisolatie, tweestapsverificatie, audit-export, SSO (OIDC), SCIM en retentie-controls.",
    },
  },
  cta: {
    title: "Zet je eerste AI-antwoord achter een goedkeuring",
    description:
      "Starter en Team zijn gratis te starten. Voor code- en deployment-workflows boek je een pilot, en bakenen we de grenzen samen met je af.",
    primary: "Maak een gratis workspace",
    secondary: "Boek een demo",
  },
  tiers: {
    monthly: "Maandelijks",
    annual: "Jaarlijks",
    twoMonthsFree: "2 maanden gratis",
    recommended: "Aanbevolen",
    allPricesNote:
      "Alle prijzen in euro's, exclusief btw (EU B2B). Jaarlijkse facturering is twee maanden gratis ten opzichte van maandelijks betalen.",
    enforcementNote:
      "Planlimieten worden via je contract gehandhaafd terwijl handhaving in het product wordt uitgerold.",
    bestFor: "Geschikt voor: ",
    everythingIn: "Alles uit {plan}, plus:",
    perMonth: "/ mnd",
    perYear: "/ jaar",
    custom: "Op maat",
    customTailored: "Afgestemd op jouw omgeving",
    annualHint: "≈ {amount} / mnd, jaarlijks gefactureerd · excl. btw",
    orPerYear: "of {amount} / jaar · excl. btw",
    customAnnual: "Jaarprijs op maat · excl. btw",
    from: "Vanaf",
    featuresColumn: "Functies",
  },
  plans: {
    starter: {
      name: "Starter",
      positioning: "AI stelt ticketantwoorden op; je team keurt elke verzending goed.",
      forWho: "Supportteams die goedkeuring-gated AI aan één helpdesk toevoegen.",
      ctaLabel: "Begin gratis",
      ctaNote: "Gratis starten. Antwoorden gaan pas na goedkeuring de deur uit.",
      limitsUsers: "Tot 3 gebruikers",
      limitsAiActions: "250 AI-acties / maand",
      f1: "1 helpdesk-/tracker-integratie: Freshdesk, Jira, Odoo of TOPdesk",
      f2: "Slack- of website-chat-intake",
      f3: "Beantwoorden van ticketvragen: open, status, prioriteit, wacht op goedkeuring",
      f4: "AI-voorgestelde antwoorden met menselijke bewerking en goedkeuring vóór verzending",
      f5: "Basis-audittijdlijn",
      f6: "Integratie-health op capability-niveau",
    },
    team: {
      name: "Team",
      positioning: "Goedgekeurde AI-antwoorden over Slack, Teams, Freshdesk en Jira.",
      forWho: "Supportteams die dagelijks werken over chat en tickettrackers.",
      ctaLabel: "Begin gratis",
      ctaNote: "Gratis starten. Koppel één echte integratie tijdens de setup.",
      limitsUsers: "Tot 10 gebruikers",
      limitsAiActions: "1.000 AI-acties / maand",
      f1: "Freshdesk-, Jira-, Odoo- + TOPdesk-integraties",
      f2: "Slack + website-chat (Microsoft Teams inbegrepen)",
      f3: "Goedgekeurde AI-antwoorden naar Freshdesk, Jira, Odoo en TOPdesk",
      f4: "Beantwoorden van ticketvragen over FixControl, Jira, Freshdesk, Odoo en TOPdesk",
      f5: "Thread-bewuste Slack/Teams-context",
      f6: "Volledige operationele tijdlijn",
      f7: "Outbox / retry / dead-letter-zichtbaarheid voor externe schrijfacties",
      f8: "Integratie-health per capability",
    },
    pro: {
      name: "Pro",
      positioning: "Van support-ticket naar gereviewde pull request.",
      forWho: "Teams die AI-voorgestelde codewijzigingen door reviewpoorten heen leveren.",
      ctaLabel: "Boek een pilot",
      ctaNote: "Pilots beginnen met het afbakenen van repository- en CI/CD-toegang.",
      limitsUsers: "Tot 25 gebruikers",
      limitsAiActions: "5.000 AI-acties / maand",
      f1: "GitHub- / GitLab-integratie",
      f2: "Engineering-missies en codebase-analyse",
      f3: "Patch- / diff-bewijs met tests, QA en security-bewijs",
      f4: "PR- / MR-workflow met goedkeuringspoorten voor risicovolle wijzigingen",
      f5: "Deployment-goedkeuringsbewijs waar geconfigureerd",
      f6: "Meerdere projecten",
      f7: "Audit-export en geavanceerde auditfuncties",
    },
    enterprise: {
      name: "Enterprise",
      positioning: "Custom policies, identity-controls en deployment-governance.",
      forWho: "Organisaties met strenge security- en audit-eisen.",
      ctaLabel: "Neem contact op",
      ctaNote: "Prijs op maat. Boek een enterprise-demo.",
      limitsUsers: "Gebruikerslimieten op maat",
      limitsAiActions: "AI-actielimieten op maat",
      f1: "SSO (OIDC) + SCIM 2.0; SAML op de roadmap",
      f2: "Custom policies en platform-admin-controls",
      f3: "Tenant- / admin-scheiding en custom retentie",
      f4: "Private deployment of dedicated omgeving, afgebakend per traject",
      f5: "Kubernetes- / Argo-deployment-governance",
      f6: "SLA- en supportvoorwaarden, plus ondersteuning bij security review",
      f7: "Onboarding op maat",
    },
  },
  comparison: {
    groups: {
      intake: { title: "Intake en ticketvragen" },
      helpdesk: { title: "Helpdesk- / tracker-workflows" },
      governance: { title: "Governance" },
      engineering: { title: "Engineering-remediatie" },
      deployment: { title: "Deployment-governance" },
      enterprise: { title: "Enterprise-controls" },
    },
    rows: {
      websiteChatIntake: { label: "Website-chat-intake" },
      slackIntake: { label: "Slack-intake" },
      teamsIntake: { label: "Microsoft Teams-intake" },
      ticketQuestionAnswering: { label: "Beantwoorden van ticketvragen" },
      threadAwareFollowups: { label: "Thread-bewuste vervolgvragen" },
      channelRedaction: { label: "Redactie in publieke / gedeelde kanalen" },
      freshdeskIntegration: { label: "Freshdesk-integratie" },
      jiraIntegration: { label: "Jira-integratie" },
      odooIntegration: { label: "Odoo-integratie" },
      topdeskIntegration: { label: "TOPdesk-integratie" },
      aiProposedReplies: { label: "AI-voorgestelde antwoorden" },
      humanEditBeforeSend: { label: "Menselijke bewerking vóór verzending" },
      freshdeskCustomerReply: { label: "Freshdesk-klantantwoord" },
      odooCustomerReply: { label: "Odoo-klantantwoord" },
      topdeskCustomerReply: { label: "TOPdesk-klantantwoord" },
      jiraInternalComment: { label: "Jira interne opmerking" },
      jsmCustomerReply: {
        label: "Jira Service Management-klantantwoord",
        caveat: "Vereist een geconfigureerd Jira Service Management-verzoektype met een gezonde klantantwoord-capability. Elk antwoord wordt safety-gecontroleerd en door iemand van je team goedgekeurd.",
      },
      ticketWriteOutbox: { label: "Ticketstatus / opmerking / schrijf-outbox" },
      approvalLedger: { label: "Goedkeuringsgrootboek" },
      actorRoleChannel: { label: "Actor / rol / kanaal / tijdstempel" },
      operationalTimeline: { label: "Operationele tijdlijn" },
      capabilityHealth: { label: "Capability-health" },
      outboxRetryDlq: { label: "Outbox / retry / dead-letter" },
      auditExport: { label: "Audit-export" },
      githubGitlab: { label: "GitHub / GitLab" },
      codebaseAnalysis: { label: "Codebase-analyse" },
      patchDiffEvidence: { label: "Patch- / diff-bewijs" },
      testsQaSecurity: { label: "Tests / QA / security-bewijs" },
      prMrWorkflow: { label: "PR- / MR-workflow" },
      riskGates: { label: "Risk-gates" },
      twoFactor: {
        label: "Tweestapsverificatie (TOTP)",
        caveat: "Authenticator-app + herstelcodes, op elk plan. FixControl-platformmedewerkers hebben het altijd nodig; je eigen admins en leden zodra je organisatie het verplicht. Geen sms.",
      },
      argoVisibility: { label: "Kubernetes / Argo-zichtbaarheid" },
      deploymentApproval: { label: "Deployment-goedkeuring" },
      promoteAbortRead: {
        label: "Promoten / afbreken / status lezen",
        caveat: "Waar geconfigureerd voor jouw Argo- / Kubernetes-setup.",
      },
      failureVisibility: { label: "Zichtbaarheid van fouten" },
      sso: {
        label: "SSO (OIDC)",
        caveat: "SAML staat op de roadmap.",
      },
      scim: {
        label: "SCIM",
        caveat: "SCIM 2.0, voor het provisioneren en deprovisioneren van gebruikers.",
      },
      customPolicies: { label: "Custom policies" },
      privateDeployment: {
        label: "Private deployment",
        caveat: "Afgebakend per traject.",
      },
      slaSecurityReview: { label: "SLA / security review" },
    },
  },
  cells: {
    whereConfigured: "waar geconfigureerd",
    whereSupported: "vereist configuratie",
    whereImplemented: "Inbegrepen",
    oneOfFreshdeskJira: "1 van Freshdesk / Jira / Odoo / TOPdesk",
    basic: "Basis",
  },
  faq: {
    q1: {
      q: "Is starten gratis?",
      a: "Ja. Starter en Team zijn gratis te starten en self-service, met demodata en één echte integratie inbegrepen. Klantgerichte antwoorden vereisen nog steeds goedkeuring.",
    },
    q2: {
      q: "Rekenen jullie per seat?",
      a: "Plannen bevatten een vast aantal gebruikers (3 op Starter, 10 op Team, 25 op Pro) in plaats van een prijs per seat. Hogere gebruikerslimieten zijn beschikbaar op Enterprise of op aanvraag.",
    },
    q3: {
      q: "Wat telt als een AI-actie?",
      a: "Eén AI-actie is één eenheid AI-werk: een voorgesteld of herschreven antwoord, een antwoord op een ticketvraag, een classificatie of een remediatie-/missiestap. Werk dat de AI uitvoert telt ook als je het resultaat afwijst; goedkeuren of afwijzen zelf telt niet, en routinematige leesacties en het bekijken van audithistorie zijn gratis. Tokens worden nooit gemeterd.",
    },
    q4: {
      q: "Kunnen we met alleen Freshdesk, Jira, Odoo of TOPdesk beginnen?",
      a: "Ja. Starter bevat één helpdesk- of tracker-integratie: Freshdesk, Jira, Odoo of TOPdesk. Team en hoger bevatten alle vier, en je kunt er meer koppelen naarmate je groeit.",
    },
    q5: {
      q: "Verstuurt FixControl antwoorden automatisch?",
      a: "Standaard niet. Freshdesk- en Jira-antwoorden vereisen altijd een menselijke goedkeuring. Voor e-mail kan een admin een policy-gescopete laag-risico-automatische-verzendmodus inschakelen, begrensd door betrouwbaarheidsdrempels, rate limits, een organisatiebreed plafond en audit-logging van elke verzending.",
    },
    q6: {
      q: "Werkt Jira-klantantwoord voor elk Jira-project?",
      a: "Klantzichtbare antwoorden vereisen Jira Service Management. Op Jira Software-projecten voegt FixControl in plaats daarvan interne issue-opmerkingen toe.",
    },
    q7: {
      q: "Wat gebeurt er als Slack, Jira of Freshdesk faalt?",
      a: "Uitgaande schrijfacties lopen via een outbox met retry- en dead-letter-afhandeling, en integratie-health wordt per capability bijgehouden. Als een schrijfactie faalt, toont FixControl de fout en precies wat het niet kon voltooien.",
    },
    q8: {
      q: "Kan FixControl naar productie deployen?",
      a: "Niet op eigen houtje. Waar Kubernetes / Argo is geconfigureerd, toont FixControl de deployment-status en routeert het de deployment-goedkeuring naar iemand van je team. Promoot-, afbreek- en leesacties draaien pas na die goedkeuring.",
    },
    q9: {
      q: "Mag de publieke website-chat interne tickets zien?",
      a: "Nee. De publieke website-chat ziet alleen publieke, klantveilige kennis en projectveilige samenvattingen, nooit de interne gegevens van je organisatie. Ticketvragen over intern werk worden alleen beantwoord in geauthenticeerde Slack-, Teams- en in-app-surfaces.",
    },
    q10: {
      q: "Kunnen we self-hosten of op een private VPS draaien?",
      a: "Ja, op Enterprise. Private deployment naar een dedicated omgeving of VPS wordt per traject afgebakend; vertel ons over je omgeving en security-eisen.",
    },
    q11: {
      q: "Bieden jullie jaarcontracten aan?",
      a: "Ja. Jaarlijkse facturering is twee maanden gratis ten opzichte van maandelijks betalen. Prijzen zijn in euro's, exclusief btw (EU B2B).",
    },
    q12: {
      q: "Ondersteunen jullie SSO/SCIM?",
      a: "SSO (OIDC) en SCIM 2.0 zijn inbegrepen op Enterprise; SAML staat op de roadmap. Vertel ons je identity provider en we bevestigen de setup.",
    },
    q13: {
      q: "Wat gebeurt er als we de maandelijkse AI-actielimiet bereiken?",
      a: "Nieuwe AI-acties worden geweigerd met een duidelijke melding totdat de limiet reset of wordt verhoogd — niets dat al loopt wordt afgebroken, en er is geen automatische overage-facturering. Limieten en eventueel overage-gedrag worden contractueel afgesproken terwijl in-product handhaving wordt uitgerold.",
    },
    q14: {
      q: "Hoe werken facturering, contracten en opzegging?",
      a: "Starter- en Team-workspaces start je gratis; betaalde plannen worden gefactureerd. Contractduur, opzegvoorwaarden en pilotvoorwaarden worden tijdens onboarding afgesproken — maandelijkse en jaarlijkse facturering zijn beide beschikbaar, en pilots worden verrekend met een jaarcontract als je doorgaat.",
    },
    q15: {
      q: "Welke support is inbegrepen?",
      a: "Elk plan bevat e-mailsupport via contact@fixcontrol.ai. Tijdens onboarding en pilots werk je rechtstreeks met het team dat het product bouwt en beheert. Enterprise-overeenkomsten voegen contractuele SLA- en supportvoorwaarden toe.",
    },
    q16: {
      q: "Kunnen we buiten de EU afnemen?",
      a: "Ja. Prijzen zijn in euro's en exclusief btw (binnen de EU geldt de B2B-verleggingsregeling). Voor organisaties buiten de EU worden facturering en belastingafhandeling tijdens onboarding bevestigd.",
    },
    q17: {
      q: "Kan FixControl met ons meegroeien?",
      a: "Ja. FixControl is ontworpen als schaalbaar multi-tenant SaaS-platform en kan meegroeien over seats, integraties, ticketvolume en governed engineering workflows. De uitrol start met begeleide onboarding omdat het platform koppelt met gevoelige operationele systemen; approval models, integration health en audit boundaries worden samen met je team gevalideerd, en gebruik wordt van daaruit uitgebreid.",
    },
  },
};
